/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { type FC, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { generateUuid, type PubSubCallback } from "@huub-web/utils";
import {
  AgentState,
  type ChildrenProp,
  Flex,
  Markdown,
  Stack,
  Text,
  usePubSub,
  useDebug,
  useDelayedState,
} from "@huub-web/ui";
import {
  useApp,
  useChatMessages,
  useWebSocketActions,
  useWebSocket,
  useAudioStreaming,
  WebSocketChannel,
  useAPI,
} from "@huub-web/backend";
import type { TemplateCollection } from "@huub-web/templating";

import { AppControllerContext } from "../../contexts";
import { useAppSettings, useNavigate } from "../../hooks";
import { AppRoutes } from "../AppRoutes/AppRoutes";
import { TemplatesProvider } from "../TemplatesProvider/TemplatesProvider";
import { AppointmentsProvider } from "../AppointmentsProvider/AppointmentsProvider";

import { routesStackStyle } from "./AppController.css";

type AppControllerProps = ChildrenProp & {
  ttsEnabled?: boolean;
  debug?: boolean;
};

/**
 * The main controller of the application.
 */
export const AppController: FC<AppControllerProps> = ({
  ttsEnabled = false,
  debug = false,
  children,
}) => {
  const { log } = useDebug();
  const {
    isConnected,
    isWaitingForAgentResponse,
    hasConnectionFailed,
    sendMessage,
    stopWaiting,
  } = useWebSocket();
  const { t } = useTranslation();
  const { $get, $post, $put, $delete } = useAPI();
  const {
    navigationState,
    isAppStateLoading,
    errorLoadingAppState,
    appCustomer,
    appCurrent,
    enabledAppFlags,
    user,
  } = useApp();
  const { isAudioEnabled, isVisualDebugEnabled } = useAppSettings();
  const {
    isPlaying: isSpeaking,
    stopPlaying: stopSpeaking,
    isAudioContextRunning,
  } = useAudioStreaming(isAudioEnabled && ttsEnabled, ttsEnabled);
  const pubSub = usePubSub();
  const { navigate } = useNavigate();

  const [hasCustomError, setHasCustomError] = useState(false);
  const [isCustomLoading, setIsCustomloading] = useState(false);

  // Initialize WebSocket
  // -------------------

  useWebSocketActions();

  // Chat messages
  // -------------------

  const {
    isStreaming: isMessageStreaming,
    hasFirstAgentResponse,
    agentResponses,
    agentResponseIDs,
    addAgentResponse,
    clearChat,
    ignoreCurrentMessages,
    conversation,
  } = useChatMessages<TemplateCollection>({
    onDuplicateMessage: (id, body) => {
      if (isVisualDebugEnabled) {
        pubSub.publish("snackbar", {
          title: "Duplicate message ID detected",
          body: (
            <Flex direction="column" gap="xs">
              <Text variant="bodyS" clamp={3}>
                A duplicate message has been send from the back-end. This should
                not happen.
              </Text>
              {body && (
                <Text variant="bodyS" color="currentColor50" clamp={3}>
                  <Markdown content={body} />
                </Text>
              )}
              <Text variant="monospace">{id}</Text>
            </Flex>
          ),
          variant: "warning",
          autoHide: false,
        });
      }
    },
  });

  // Agent state
  // -------------------

  const agentState: AgentState = (() => {
    if (isSpeaking && isAudioEnabled) {
      return AgentState.SPEAKING;
    }
    if (isMessageStreaming) {
      return AgentState.TYPING;
    }
    if (hasCustomError) {
      return AgentState.ERROR;
    }
    if (hasConnectionFailed) {
      return AgentState.ERROR;
    }
    if (!isConnected || isWaitingForAgentResponse || isCustomLoading) {
      return AgentState.LOADING;
    }
    return AgentState.IDLE;
  })();

  useEffect(() => {
    if (!hasFirstAgentResponse && agentState === AgentState.ERROR) {
      addAgentResponse({
        id: generateUuid(),
        body: t("vite.intro.error"),
      });
    } else if (hasFirstAgentResponse && agentState === AgentState.ERROR) {
      addAgentResponse({
        id: generateUuid(),
        body: t("general.chatError"),
      });
    }
  }, [agentState, hasFirstAgentResponse, t]);

  const stopAll = () => {
    if (isSpeaking) {
      stopSpeaking();
    }
    ignoreCurrentMessages();
    sendMessage(WebSocketChannel.STOP_ALL, {});
    stopWaiting();
    pubSub.publish("onStopAll", {});
  };

  const currentAgentResponse =
    agentResponses[agentResponseIDs?.[agentResponseIDs.length - 1]] ?? null;

  // API requests
  // -------------------

  useEffect(() => {
    const apiRequestCallback: PubSubCallback<{
      method: "GET" | "POST" | "PUT" | "DELETE";
      endpoint: string;
      payload?: Record<string, any> | undefined;
    }> = ({ method, endpoint: url, payload }) => {
      log("brown", "AppController", "handleAPIRequest", method, url, payload);

      const handleError = (error: Error) => {
        console.error("[AppController] handleAPIRequest", error);
        setIsCustomloading(false);

        pubSub.publish("snackbar", {
          body: t("general.error"),
          variant: "error",
        });
      };

      const handleSuccess = (response: {
        data: { success: boolean; notify_via_websocket?: boolean };
      }) => {
        log("brown", "AppController", "APIRequest handleSuccess", response);
        setIsCustomloading(false);

        if (!response.data.success && !response.data.notify_via_websocket) {
          pubSub.publish("snackbar", {
            body: t(
              method === "DELETE" ? "general.deleteError" : "general.error",
            ),
            variant: "error",
          });
        } else if (response.data.success && method === "DELETE") {
          pubSub.publish("snackbar", {
            body: t("general.deleteSuccess"),
            variant: "success",
          });
        }
      };

      setIsCustomloading(true);

      const handleRequest = () => {
        switch (method) {
          case "GET":
            $get(url, payload).then(handleSuccess).catch(handleError);
            break;
          case "POST":
            $post(url, payload).then(handleSuccess).catch(handleError);
            break;
          case "PUT":
            $put(url, payload).then(handleSuccess).catch(handleError);
            break;
          case "DELETE":
            $delete(url, payload).then(handleSuccess).catch(handleError);
            break;
        }
      };

      setTimeout(handleRequest, 350);
    };

    const unsubscribe = pubSub.subscribe("apiRequest", apiRequestCallback);

    return () => unsubscribe();
  }, [$get, $post, $put, $delete]);

  // Path navigation
  // Used for internal links in the markdown component
  // ---------------

  useEffect(() => {
    const unsubscribe = pubSub.subscribe("navigateToPath", ({ path }) => {
      navigate(path);
    });

    return () => unsubscribe();
  }, [navigate]);

  /**
   * Lock UI state is delayed by 200ms to prevent flickering.
   */

  const [lockUI, setLockUI, setImmediateLockUI] = useDelayedState(false, 200);

  useEffect(() => {
    if (isAppStateLoading || isCustomLoading || !isConnected) {
      setImmediateLockUI(true);
    } else {
      setLockUI(false);
    }
  }, [isAppStateLoading, isCustomLoading, isConnected]);

  return (
    <AppControllerContext.Provider
      value={{
        ttsEnabled,

        // AppContext
        navigationState,
        isAppStateLoading,
        errorLoadingAppState,
        appCustomer,
        appCurrent,
        enabledAppFlags,
        user,
        activateCustomErrorState: () => setHasCustomError(true),
        deactivateCustomErrorState: () => setHasCustomError(false),
        activateCustomLoadingState: () => setIsCustomloading(true),
        deactivateCustomLoadingState: () => setIsCustomloading(false),
        isLoading: isAppStateLoading || isCustomLoading,
        isCustomLoading,
        stopAll,

        // useChatMessages
        isMessageStreaming,
        agentResponses,
        agentResponseIDs,
        addAgentResponse,
        clearChat,
        conversation,

        // AgentStateContext
        state: agentState,
        isWaitingForAgentResponse,
        currentAgentResponse,

        // useAudioStreaming
        isSpeaking,
        stopSpeaking,
        isAudioContextRunning,
      }}
    >
      <AppointmentsProvider>
        <TemplatesProvider
          lockPrompts={isWaitingForAgentResponse || !isConnected}
          lockUI={lockUI}
        >
          <Stack data-stack="routes" className={routesStackStyle}>
            <AppRoutes debug={debug}>{children}</AppRoutes>
          </Stack>
        </TemplatesProvider>
      </AppointmentsProvider>
    </AppControllerContext.Provider>
  );
};
